import { Box, Flex, Text, ThemeProvider } from 'mds-web-ui'
import { motion } from 'motion/react'
import { darken, rem, transparentize } from 'polished'
import PropTypes from 'prop-types'
import React, {
  createContext,
  Suspense,
  useContext,
  useMemo,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { getContrastingBorder, lightenIfPoss } from '../../utils/color'
import { DeadlineIcon, FatTick, ReminderIcon } from './icons'
import { Link } from './Link'
import { DeterminateProgress, IndeterminateProgress } from './SubtasksProgress'
import system from './system'
export const width = 300

const ActionCardHoverContext = createContext(false)
const DEFAULT_LIFT = { skipEnter: false, lifted: false }
const ActionCardLiftContext = createContext(DEFAULT_LIFT)

const ActionPriority = React.lazy(
  () => import(/* webpackChunkName: "actionpriority" */ './ActionPriority'),
)

export const cardBorderRadius = '13px'

const FlexWithNoOpen = React.forwardRef(({ isOpen, ...props }, ref) => (
  <Flex ref={ref} {...props} />
))

const hoverDarken = 0.02

const MotionFlexCard = motion.create(FlexWithNoOpen)

const getBoxShadow = ({ inSchedule, isDragging, bg }) => {
  // contrasting border for low-luminance themes
  const contrasting = getContrastingBorder(bg)

  // shadow when dragged
  const dragging = isDragging ? '2px 5px 20px 0px rgb(0 0 0 / 30%)' : null

  // both null
  if (contrasting === 'initial' && dragging == null) {
    return null
  }

  if (contrasting === 'initial') {
    return dragging
  }

  if (dragging == null) {
    return contrasting
  }

  // combine
  return `${contrasting}, ${dragging}`
}

export const CardWrapper = React.forwardRef(
  ({ children, bg, isOpen, isDragging, inSchedule, index, ...props }, ref) => {
    const cardHovered = useContext(ActionCardHoverContext)
    const { skipEnter, lifted } = useContext(ActionCardLiftContext)
    const backgroundColor = cardHovered
      ? darken(isOpen ? 0.13 : hoverDarken, bg)
      : isOpen
        ? darken(0.1, bg)
        : bg
    return (
      <MotionFlexCard
        alignItems="center"
        justifyContent="center"
        width={width}
        fontSize={rem(16)}
        px={19}
        py={19}
        css={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',

          position: 'relative',
          borderRadius: cardBorderRadius,
          minHeight: '80px',

          boxShadow: getBoxShadow({ inSchedule, isDragging, bg }),

          textDecoration: 'none',

          backfaceVisibility: 'hidden',

          overflowWrap: 'break-word',

          fontWeight: 500,
        }}
        ref={ref}
        bg={bg}
        isOpen={isOpen}
        // Participates in the parent's layout projection so the reorder glide
        // isn't cancelled by this nested motion component. Off while lifted so
        // it doesn't fight the drag transform or the portal remount.
        layout={lifted ? false : 'position'}
        // only re-measure/animate layout when the card's position (index)
        // actually changes - e.g. on reorder/insert - not on every hover
        // re-render. Without this the layout spring fires spuriously on hover
        // (a small horizontal shift + bounce) when the app content is scaled
        // (menu open) because the projected measurement differs subtly.
        layoutDependency={index}
        // fade + subtle pop on enter (suppressed on first paint by the
        // AnimatePresence initial={false} in ActionsList, so only newly-added
        // cards animate in). scale/opacity are used instead of x/y so they don't
        // fight the layout-position transform or the drag transform.
        initial={skipEnter ? false : { opacity: 0, scale: 0.96 }}
        animate={{ backgroundColor, opacity: 1, scale: 1 }}
        transition={{
          duration: 0.2,
          layout: { type: 'spring', stiffness: 550, damping: 45 },
        }}
        {...props}
      >
        {children}
      </MotionFlexCard>
    )
  },
)

const MetaText = styled(Text)({
  fontSize: rem(10),
  textTransform: 'uppercase',
  letterSpacing: '-0.03em',
})

const ListMetaLabel = ({ list }) => {
  if (!list) return null

  return (
    <MetaText mt="4px" fontWeight={500}>
      {list}
    </MetaText>
  )
}

const DateMetaLabel = ({ date, repeat, hasReminder, reminderTime }) => {
  if (date == null && repeat == null) return null

  return (
    <MetaText
      mt="2px"
      lineHeight="1"
      css={{ display: 'flex', alignItems: 'center' }}
      fontWeight="normal"
    >
      {hasReminder && (
        <>
          <Box as="span" mt="3px" mr={1}>
            <ReminderIcon size={10} />
          </Box>
          {reminderTime && (
            <Box as="span" mr={1}>
              {reminderTime}
            </Box>
          )}
        </>
      )}
      {date && <Text as="span">{date}</Text>}
      {repeat && (
        <Text as="span">
          &nbsp;
          {`(${repeat})`}
        </Text>
      )}
    </MetaText>
  )
}

const DeadlineMetaLabel = ({ deadline }) => {
  if (!deadline) return null

  return (
    <MetaText
      mt="4px"
      lineHeight="1"
      css={{ display: 'flex', alignItems: 'center' }}
      fontWeight="normal"
    >
      <Box as="span" mr="3px">
        <DeadlineIcon size={8} />
      </Box>
      {deadline}
    </MetaText>
  )
}

const MetaLabels = ({
  listName,
  displayDate,
  displayRepeat,
  displayDeadline,
  hasReminder,
  displayReminderTime,
  scheduleDisplayIndex,
  createdAt,
}) => {
  if (!(
    listName ||
    displayDate ||
    displayRepeat ||
    displayDeadline ||
    scheduleDisplayIndex != null
  ))
    return null

  return (
    <>
      <ListMetaLabel list={listName} />
      <DateMetaLabel
        date={displayDate}
        repeat={displayRepeat}
        hasReminder={hasReminder}
        reminderTime={displayReminderTime}
      />
      <DeadlineMetaLabel deadline={displayDeadline} />
      {scheduleDisplayIndex != null && (
        <MetaText
          mt="2px"
          fontWeight="normal"
          css={{ color: '#888', fontSize: '8px' }}
        >
          idx: {scheduleDisplayIndex} | created:{' '}
          {createdAt?.toDate
            ? createdAt.toDate().toLocaleDateString()
            : createdAt instanceof Date
              ? createdAt.toLocaleDateString()
              : JSON.stringify(createdAt)?.slice(0, 20)}
        </MetaText>
      )}
    </>
  )
}

const ProgressBar = React.memo(
  ({ subtasksCompleted: completed, subtasksTotal: total, color }) => {
    if (!total) return null

    return (
      <Flex
        mt={2}
        // use a fixed width not a %, otherwise it gets narrower when priority icon is shown
        width={120}
        ml={0}
        mr="auto"
        role="progressbar"
        aria-valuenow={completed}
        aria-valuemin="0"
        aria-valuemax={total}
        aria-label="Subtasks progress"
      >
        {total <= 10 ? (
          <DeterminateProgress
            total={total}
            completed={completed}
            color={color}
            height={3}
          />
        ) : (
          <IndeterminateProgress
            total={total}
            completed={completed}
            color={color}
            height={3}
          />
        )}
      </Flex>
    )
  },
)

// Link is a system() component, which drops the `css` prop, so the focus ring
// has to be a real styled rule. On focus we hide the default (blue) outline and
// draw a thick inset ring on the card itself.
const CardLink = styled(Link)`
  &:focus {
    outline: none;
  }
  &:focus > div {
    box-shadow: inset 0 0 0 4px ${(props) => props.$focusColor};
  }
`

const CardFront = React.memo(
  ({
    bg = 'white',
    color = '#696969',
    title,
    listName,
    displayDate,
    displayRepeat,
    displayDeadline,
    subtasksCompleted,
    subtasksTotal,
    isCompleted,
    isOpen,
    isDragging,
    inSchedule,
    hasReminder,
    displayReminderTime,
    scheduleDisplayIndex,
    createdAt,
    href,
    dragHandleProps,
    dragHandleRef,
    priority,
    ...rest
  }) => (
    <CardLink
      to={href}
      ref={dragHandleRef}
      draggable={false}
      {...dragHandleProps}
      // Required to be :focus (:focus-visible doesn't work here)
      $focusColor={transparentize(0.5, color)}
      aria-label={title}
    >
      <CardWrapper
        isOpen={isOpen}
        isDragging={isDragging}
        inSchedule={inSchedule}
        bg={bg}
        color={color}
        {...rest}
      >
        <Flex
          flex={1}
          flexDirection="column"
          css={{
            // fixes word-break on child
            minWidth: '1%',
          }}
        >
          <Text
            css={{
              textDecoration: isCompleted ? 'line-through' : null,
              overflowWrap: 'break-word',
              letterSpacing: '-0.01em',
            }}
            role="presentation"
          >
            {title}
          </Text>

          <MetaLabels
            listName={listName}
            displayDate={displayDate}
            displayRepeat={displayRepeat}
            displayDeadline={displayDeadline}
            hasReminder={hasReminder}
            displayReminderTime={displayReminderTime}
            scheduleDisplayIndex={scheduleDisplayIndex}
            createdAt={createdAt}
          />

          <ProgressBar
            subtasksCompleted={subtasksCompleted}
            subtasksTotal={subtasksTotal}
            color={color}
          />
        </Flex>
        <Suspense fallback={null}>
          <ActionPriority
            color={color}
            priority={priority}
            animated
            hideWhenPriorityNotSet
            ml={2}
          />
        </Suspense>
      </CardWrapper>
    </CardLink>
  ),
)
CardFront.displayName = 'CardFront'

CardFront.propTypes = {
  isCompleted: PropTypes.bool,
}

const iconTransition = {
  opacity: { duration: 0.05 },
  scale: {
    type: 'spring',
    stiffness: 700,
    damping: 25,
    mass: 1,
  },
}

const MotionIcon = motion.create(FlexWithNoOpen)

const circleSize = 36

const IconLink = React.memo(({ children, onClick, isOpen, bg, ariaLabel }) => {
  const cardHovered = useContext(ActionCardHoverContext)
  const [iconHovered, setIconHovered] = useState(false)
  const base = darken(isOpen ? 0.13 : hoverDarken, bg)
  const show = cardHovered || iconHovered
  const direct = iconHovered
  const animate = !show
    ? {
        opacity: 0,
        scale: 0.2,
        boxShadow: `0 0 0 4px ${transparentize(1, base)}`,
        backgroundColor: lightenIfPoss(0.08, base),
      }
    : direct
      ? {
          opacity: 1,
          scale: 1.05,
          boxShadow: `0 0 0 3px ${base}`,
          backgroundColor: lightenIfPoss(0.1, base),
        }
      : {
          opacity: 1,
          scale: 1,
          boxShadow: `0 0 0 4px ${base}`,
          backgroundColor: lightenIfPoss(0.08, base),
        }
  return (
    <Flex
      p={3}
      color="inherit"
      alignItems="center"
      justifyContent="center"
      tabIndex={-1}
      onMouseDown={(e) => e.preventDefault()}
      css={{
        userSelect: 'none',
        pointerEvents: 'all',
      }}
    >
      <MotionIcon
        alignItems="center"
        justifyContent="center"
        bg={bg}
        isOpen={isOpen}
        aria-label={ariaLabel}
        // this tick is an aria-hidden, hover/click affordance - keep it out of
        // the tab order (motion would otherwise add tabIndex=0 because of
        // whileTap, creating a phantom focus stop with no visible ring)
        tabIndex={-1}
        initial={{ opacity: 0, scale: 0.2 }}
        animate={animate}
        transition={iconTransition}
        whileTap={{ scale: 0.85 }}
        onHoverStart={() => setIconHovered(true)}
        onHoverEnd={() => setIconHovered(false)}
        css={{
          cursor: 'pointer',
          borderRadius: `${circleSize / 2}px`,
          width: `${circleSize}px`,
          height: `${circleSize}px`,
          '&:focus': { backgroundColor: 'rgba(1,1,1,0.07)', outline: 'none' },
          '&:active': { backgroundColor: 'rgba(0,0,0,0.07)' },
        }}
        onClick={onClick}
      >
        {children}
      </MotionIcon>
    </Flex>
  )
})

const CardUnder = React.memo(
  ({ toggleComplete, clickDelete, isOpen, isCompleted, color, bg, style }) => (
    <Flex
      justifyContent="flex-end"
      css={{
        position: 'absolute',
        left: 0,
        right: 0,
        top: '1px',
        bottom: '1px',
        borderRadius: cardBorderRadius,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
      style={style}
      color={color}
      aria-hidden={true}
    >
      {/* <IconLink
        onClick={clickDelete}
        isOpen={isOpen}
        bg={bg}
        ariaLabel="Delete"
      >
        <DeleteActionIcon size={16} />
      </IconLink> */}

      <IconLink
        onClick={toggleComplete}
        isOpen={isOpen}
        bg={bg}
        ariaLabel="Complete"
      >
        <FatTick size={20} checked={isCompleted} strokeWidth={5} />
      </IconLink>
    </Flex>
  ),
)

CardUnder.propTypes = {
  toggleComplete: PropTypes.func,
}

CardUnder.displayName = 'CardUnder'

const Relative = system(
  {
    extend: Box,
    // padding not margin - margin collapsing causes animation glitch on drag and drop
    py: '1px',
  },
  {
    position: 'relative',
    userSelect: 'none',
  },
  'space',
  'maxWidth',
)

const ActionCardRoot = React.forwardRef(
  ({ children, isDragging, style, ...draggableProps }, ref) => {
    const [cardHovered, setCardHovered] = useState(false)
    const [skipEnter, setSkipEnter] = useState(false)
    // hello-pangea keeps the node in-tree with position:fixed. LeftPane/Lists
    // overflow then clips it, and the neighbouring pane paints on top (dnd-8).
    // Reparent to document.body for the whole lift, including drop animation.
    const isLifted = Boolean(isDragging || style?.position === 'fixed')
    if (isLifted && !skipEnter) setSkipEnter(true)

    const liftValue = useMemo(
      () => ({ skipEnter, lifted: isLifted }),
      [skipEnter, isLifted],
    )

    const node = (
      <Relative
        {...draggableProps}
        style={{ ...style, ...(isLifted ? { zIndex: 5000 } : null) }}
        ref={ref}
        onMouseEnter={() => setCardHovered(true)}
        onMouseLeave={() => setCardHovered(false)}
      >
        {children}
      </Relative>
    )

    return (
      <ActionCardLiftContext.Provider value={liftValue}>
        <ActionCardHoverContext.Provider value={cardHovered}>
          {isLifted
            ? createPortal(<ThemeProvider>{node}</ThemeProvider>, document.body)
            : node}
        </ActionCardHoverContext.Provider>
      </ActionCardLiftContext.Provider>
    )
  },
)

const ActionCard = React.memo(
  React.forwardRef(
    (
      {
        draggableProps,
        dragHandleProps,
        dragHandleRef,
        style,
        color,
        toggleComplete,
        isCompleted,
        clickDelete,
        index,
        isOpen,
        isDragging = false,
        inSchedule = false,
        bg,
        ...rest
      },
      ref,
    ) => (
      <ActionCardRoot {...draggableProps} isDragging={isDragging} ref={ref}>
        <CardFront
          bg={bg}
          color={color}
          dragHandleProps={dragHandleProps}
          dragHandleRef={dragHandleRef}
          style={style}
          isOpen={isOpen}
          inSchedule={inSchedule}
          isDragging={isDragging}
          index={index}
          {...rest}
        />
        <CardUnder
          bg={bg}
          color={color}
          isOpen={isOpen}
          isCompleted={isCompleted}
          toggleComplete={toggleComplete}
          clickDelete={clickDelete}
          style={style && style.opacity < 1 ? { display: 'none' } : null}
        />
      </ActionCardRoot>
    ),
  ),
)
ActionCard.displayName = 'ActionCard'

ActionCard.propTypes = {
  draggableProps: PropTypes.object,
  dragHandleProps: PropTypes.object,
  dragHandleRef: PropTypes.object,
  // action styling
  style: PropTypes.object,
  color: PropTypes.string,
  // props for scroll animation
  index: PropTypes.number,
  // defined on front/under
  ...CardFront.propTypes,
  ...CardUnder.propTypes,
}

// const ScrollableActionCard = withScrollAnimation(ActionCard)
// export default ScrollableActionCard
export default ActionCard
